import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import BreadcrumbStructuredData from "@/components/BreadcrumbStructuredData";
import PortfolioStructuredData from "@/components/PortfolioStructuredData";
import Breadcrumb from "@/components/Breadcrumb";
import { Calculator, FileText, Workflow, ExternalLink, Clock, Users, CheckCircle, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { supportedLanguages, type SupportedLanguage } from "@/lib/i18n";
import { generateLocalizedUrl } from "@/lib/slugUtils";
import FilloutButton from "@/components/FilloutButton";

const Portfolio = () => {
  const { t } = useTranslation(['portfolio', 'common']);
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: SupportedLanguage }>();
  const currentLang = lang && supportedLanguages.includes(lang) ? lang : 'en';
  const projects = [
    {
      category: "calculators",
      icon: Calculator,
      title: t('portfolio:projects.0.title'),
      description: t('portfolio:projects.0.description'),
      longDescription: t('portfolio:projects.0.longDescription'),
      technologies: (t('portfolio:projects.0.technologies', { returnObjects: true }) as string[]) || [],
      industry: t('portfolio:projects.0.industry'),
      timeline: t('portfolio:projects.0.timeline'),
      impact: t('portfolio:projects.0.impact'),
      imageUrl: "/placeholder.svg?height=300&width=500",
      popular: t('portfolio:projects.0.popular'),
      liveDemo: t('portfolio:projects.0.liveDemo')
    },
    {
      category: "calculators",
      icon: Calculator,
      title: t('portfolio:projects.1.title'),
      description: t('portfolio:projects.1.description'),
      longDescription: t('portfolio:projects.1.longDescription'),
      technologies: (t('portfolio:projects.1.technologies', { returnObjects: true }) as string[]) || [],
      industry: t('portfolio:projects.1.industry'),
      timeline: t('portfolio:projects.1.timeline'),
      impact: t('portfolio:projects.1.impact'),
      imageUrl: "/placeholder.svg?height=300&width=500",
      popular: false,
      liveDemo: false
    },
    {
      category: "calculators",
      icon: Calculator,
      title: t('portfolio:projects.2.title'),
      description: t('portfolio:projects.2.description'),
      longDescription: t('portfolio:projects.2.longDescription'),
      technologies: (t('portfolio:projects.2.technologies', { returnObjects: true }) as string[]) || [],
      industry: t('portfolio:projects.2.industry'),
      timeline: t('portfolio:projects.2.timeline'),
      impact: t('portfolio:projects.2.impact'),
      imageUrl: "/placeholder.svg?height=300&width=500",
      popular: false,
      liveDemo: false
    },
    {
      category: "forms",
      icon: FileText,
      title: t('portfolio:projects.3.title'),
      description: t('portfolio:projects.3.description'),
      longDescription: t('portfolio:projects.3.longDescription'),
      technologies: (t('portfolio:projects.3.technologies', { returnObjects: true }) as string[]) || [],
      industry: t('portfolio:projects.3.industry'),
      timeline: t('portfolio:projects.3.timeline'),
      impact: t('portfolio:projects.3.impact'),
      imageUrl: "/placeholder.svg?height=300&width=500",
      popular: false,
      liveDemo: true
    },
    {
      category: "forms", 
      icon: FileText,
      title: t('portfolio:projects.4.title'),
      description: t('portfolio:projects.4.description'),
      longDescription: t('portfolio:projects.4.longDescription'),
      technologies: (t('portfolio:projects.4.technologies', { returnObjects: true }) as string[]) || [],
      industry: t('portfolio:projects.4.industry'),
      timeline: t('portfolio:projects.4.timeline'),
      impact: t('portfolio:projects.4.impact'),
      imageUrl: "/placeholder.svg?height=300&width=500",
      popular: false,
      liveDemo: true
    },
    {
      category: "workflows",
      icon: Workflow,
      title: t('portfolio:projects.5.title'),
      description: t('portfolio:projects.5.description'),
      longDescription: t('portfolio:projects.5.longDescription'),
      technologies: (t('portfolio:projects.5.technologies', { returnObjects: true }) as string[]) || [],
      industry: t('portfolio:projects.5.industry'),
      timeline: t('portfolio:projects.5.timeline'),
      impact: t('portfolio:projects.5.impact'),
      imageUrl: "/placeholder.svg?height=300&width=500",
      popular: false,
      liveDemo: false
    }
  ];

  const caseStudies = [
    {
      title: t('portfolio:caseStudies.0.title'),
      client: t('portfolio:caseStudies.0.client'),
      challenge: t('portfolio:caseStudies.0.challenge'),
      solution: t('portfolio:caseStudies.0.solution'),
      results: t('portfolio:caseStudies.0.results', { returnObjects: true }) as string[],
      testimonial: t('portfolio:caseStudies.0.testimonial'),
      industry: t('portfolio:caseStudies.0.industry')
    },
    {
      title: t('portfolio:caseStudies.1.title'),
      client: t('portfolio:caseStudies.1.client'),
      challenge: t('portfolio:caseStudies.1.challenge'),
      solution: t('portfolio:caseStudies.1.solution'),
      results: t('portfolio:caseStudies.1.results', { returnObjects: true }) as string[],
      testimonial: t('portfolio:caseStudies.1.testimonial'),
      industry: t('portfolio:caseStudies.1.industry')
    }
  ];

  const technologies = t('portfolio:technologies', { returnObjects: true }) as string[];

  const stats = [
    { label: t('portfolio:stats.0.label'), value: t('portfolio:stats.0.value') },
    { label: t('portfolio:stats.1.label'), value: t('portfolio:stats.1.value') },
    { label: t('portfolio:stats.2.label'), value: t('portfolio:stats.2.value') },
    { label: t('portfolio:stats.3.label'), value: t('portfolio:stats.3.value') }
  ];

  return (
    <div className="min-h-screen">
      <SEOHead page="portfolio" />
      <BreadcrumbStructuredData />
      <PortfolioStructuredData projects={projects} />
      <Header />
      
      {/* Breadcrumb Navigation */}
      <div className="container mx-auto px-4 py-4">
        <Breadcrumb />
      </div>
      
      {/* Hero Section */}
      <section className="py-20 bg-gradient-hero text-primary-foreground">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Badge variant="outline" className="mb-6 border-primary-foreground text-primary-foreground">
              {t('portfolio:hero.badge')}
            </Badge>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              {t('portfolio:hero.title')}
              <span className="block text-secondary">{t('portfolio:hero.subtitle')}</span>
            </h1>
            <p className="text-xl mb-8 text-primary-foreground/90 max-w-2xl mx-auto">
              {t('portfolio:hero.description')}
            </p>
            
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl font-bold text-secondary mb-2">{stat.value}</div>
                  <div className="text-sm text-primary-foreground/80">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Projects */}
      <section className="py-20 bg-gradient-subtle">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">{t('portfolio:projectsSection.badge')}</Badge>
            <h2 className="text-4xl font-bold mb-6">{t('portfolio:projectsSection.title')}</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('portfolio:projectsSection.description')}
            </p>
          </div>
          
          <Tabs defaultValue="calculators" className="max-w-7xl mx-auto">
            <TabsList className="grid w-full grid-cols-3 mb-8">
              <TabsTrigger value="calculators">{t('portfolio:tabs.calculators')}</TabsTrigger>
              <TabsTrigger value="forms">{t('portfolio:tabs.forms')}</TabsTrigger>
              <TabsTrigger value="workflows">{t('portfolio:tabs.workflows')}</TabsTrigger>
            </TabsList>
            
            {["calculators", "forms", "workflows"].map((category) => (
              <TabsContent key={category} value={category}>
                <div className="grid lg:grid-cols-2 gap-8">
                  {projects.filter(p => p.category === category).map((project, index) => {
                    const IconComponent = project.icon;
                    return (
                      <Card key={index} className="relative hover:shadow-elegant transition-smooth group">
                        {project.popular && (
                          <Badge className="absolute -top-3 left-4 bg-accent text-accent-foreground z-10">
                            {t('common:popular')}
                          </Badge>
                        )}
                        
                        {/* Project Image */}
                        <div className="relative h-48 bg-muted rounded-t-lg overflow-hidden">
                          <img 
                            src={project.imageUrl} 
                            alt={project.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                          <div className="absolute inset-0 bg-navy-primary/20 group-hover:bg-navy-primary/10 transition-colors" />
                        </div>
                        
                        <CardHeader>
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center space-x-3">
                              <div className="flex items-center justify-center w-12 h-12 bg-accent/10 rounded-lg group-hover:bg-accent group-hover:text-accent-foreground transition-smooth">
                                <IconComponent className="w-6 h-6 text-accent group-hover:text-accent-foreground" />
                              </div>
                              <div>
                                <CardTitle className="text-xl">{project.title}</CardTitle>
                                <p className="text-sm text-muted-foreground">{project.industry}</p>
                              </div>
                            </div>
                            {project.liveDemo && (
                              <Badge variant="secondary" className="text-xs">
                                {t('portfolio:liveDemo')}
                              </Badge>
                            )}
                          </div>
                        </CardHeader>
                        
                        <CardContent className="space-y-6">
                          <CardDescription className="text-base leading-relaxed">
                            {project.longDescription}
                          </CardDescription>
                          
                          {/* Project Details */}
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div className="flex items-center space-x-2">
                              <Clock className="w-4 h-4 text-muted-foreground" />
                              <span className="text-muted-foreground">{t('portfolio:timeline')}:</span>
                              <span className="font-medium">{project.timeline}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <CheckCircle className="w-4 h-4 text-green-600" />
                              <span className="text-muted-foreground">{t('portfolio:impact')}:</span>
                              <span className="font-medium text-green-600">{project.impact}</span>
                            </div>
                          </div>
                          
                          {/* Technologies */}
                          <div>
                            <p className="text-sm font-medium mb-2">{t('portfolio:technologiesUsed')}:</p>
                            <div className="flex flex-wrap gap-1">
                              {project.technologies.map((tech, techIndex) => (
                                <Badge key={techIndex} variant="outline" className="text-xs">
                                  {tech}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          
                          {/* Action Buttons */}
                          <div className="flex space-x-3 pt-2">
                            {project.liveDemo && (
                              <Button size="sm" className="flex-1">
                                <ExternalLink className="w-4 h-4 mr-2" />
                                {t('portfolio:viewDemo')}
                              </Button>
                            )}
                            <Button size="sm" variant="outline" className="flex-1">
                              {t('portfolio:caseStudy')}
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">{t('portfolio:caseStudiesSection.badge')}</Badge>
            <h2 className="text-4xl font-bold mb-6">{t('portfolio:caseStudiesSection.title')}</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('portfolio:caseStudiesSection.description')}
            </p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {caseStudies.map((study, index) => (
              <Card key={index} className="hover:shadow-elegant transition-smooth">
                <CardHeader>
                  <div className="flex items-center justify-between mb-4">
                    <Badge variant="secondary">{study.industry}</Badge>
                    <span className="text-sm text-muted-foreground">{study.client}</span>
                  </div>
                  <CardTitle className="text-xl">{study.title}</CardTitle>
                </CardHeader>
                
                <CardContent className="space-y-6">
                  <div>
                    <h4 className="font-semibold mb-2 text-destructive">{t('portfolio:challenge')}</h4>
                    <p className="text-muted-foreground">{study.challenge}</p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold mb-2 text-primary">{t('portfolio:solution')}</h4>
                    <p className="text-muted-foreground">{study.solution}</p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold mb-3">{t('portfolio:results')}</h4>
                    <ul className="space-y-2">
                      {study.results.map((result, resultIndex) => (
                        <li key={resultIndex} className="flex items-start space-x-2">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                          <span className="text-muted-foreground">{result}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="bg-muted p-4 rounded-lg">
                    <p className="italic text-muted-foreground">"{study.testimonial}"</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="py-20 bg-gradient-subtle">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">{t('portfolio:technologyStack.badge')}</Badge>
            <h2 className="text-4xl font-bold mb-6">{t('portfolio:technologyStack.title')}</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('portfolio:technologyStack.description')}
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
              {technologies.map((tech, index) => (
                <div
                  key={index}
                  className="bg-card p-4 rounded-lg text-center hover:shadow-elegant transition-smooth hover:scale-105"
                >
                  <span className="text-sm font-medium">{tech}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-hero text-primary-foreground">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">
              {t('portfolio:cta.title')}
            </h2>
            <p className="text-xl mb-8 text-primary-foreground/90">
              {t('portfolio:cta.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <FilloutButton size="lg" variant="secondary" className="text-lg px-8 py-3">
                {t('portfolio:cta.primaryButton')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </FilloutButton>
              <Button 
                size="lg" 
                variant="outline" 
                className="text-lg px-8 py-3 border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                onClick={() => navigate(generateLocalizedUrl('contact', currentLang))}
              >
                {t('portfolio:cta.secondaryButton')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Portfolio;